import type { TFunction } from 'i18next'
import { formatPhoneNumberForBlocking } from '~/atoms/BlockedNumbersState'
import { getFilterItemKey } from '~/components/shared/filterSelection'
import { FilterItem, FilterSection, SelectedFilterData } from '~/types/Shared'

import { PhoneNumber, User } from '@mangovoice/types'

export interface FollowUpUserItem extends FilterItem {
  title: string
  data: number // user ID
}

export interface MissedCallReturnStatusItem extends FilterItem {
  title: string
  data: string // API status value
}

export interface PhoneNumberItem extends FilterItem {
  title: string
  data: string // formatted phone number without country code
}

export interface MissedCallTypeItem extends FilterItem {
  title: string
  data: string // API missed_type value
}

export interface MissedCallFilterParams {
  missed_type?: string[] // Array of missed_type values
  missed_call_return_status?: string[]
  follow_up_users?: number[]
  to_formatted?: string[] // Array of phone numbers for multi-select
}

export const getDefaultFilterData = (
  t: TFunction,
  pbxUsers: User[] = [],
  phoneNumbers: PhoneNumber[] = [],
): FilterSection[] => {
  const typeOptions: MissedCallTypeItem[] = [
    {
      title: t('missedCalls.filters.type.voicemail', 'Voicemail'),
      data: 'voicemail',
    },
    {
      title: t('missedCalls.filters.type.abandoned', 'Abandoned'),
      data: 'abandoned',
    },
  ]

  const statusOptions: MissedCallReturnStatusItem[] = [
    {
      title: t('missedCalls.followUpStatus.open', 'Open'),
      data: 'open',
    },
    {
      title: t(
        'missedCalls.followUpStatus.returnedCallBack',
        'Returned - Call back',
      ),
      data: 'returned_callback',
    },
    {
      title: t('missedCalls.followUpStatus.returnedSMS', 'Returned - SMS'),
      data: 'returned_sms',
    },
    {
      title: t(
        'missedCalls.followUpStatus.returnedVoicemail',
        'Returned - Voicemail',
      ),
      data: 'returned_voicemail',
    },
    {
      title: t('missedCalls.followUpStatus.returnedEmail', 'Returned - Email'),
      data: 'returned_email',
    },
    {
      title: t(
        'missedCalls.followUpStatus.returnedNoAnswer',
        'Returned - No answer',
      ),
      data: 'returned_no_answer',
    },
    {
      title: t(
        'missedCalls.followUpStatus.noFollowUpRequired',
        'No follow up required',
      ),
      data: 'no_follow_up_required',
    },
  ]

  const userItems: FollowUpUserItem[] = pbxUsers.map((user) => ({
    title:
      user.full_name ||
      t('missedCalls.filters.userFallback', 'User {{id}}', { id: user.id }),
    data: user.id,
  }))

  const phoneNumberItems: PhoneNumberItem[] = phoneNumbers
    .map((phone) => {
      const rawNumber = phone.number_for_display || phone.number
      return {
        title: phone.name || phone.number_for_display || phone.number,
        data: formatPhoneNumberForBlocking(rawNumber),
      }
    })
    .sort((a, b) => {
      // Alphabetize by name (title), with null/undefined handling
      const titleA = a.title?.trim() || ''
      const titleB = b.title?.trim() || ''
      return titleA.localeCompare(titleB, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    })

  return [
    {
      title: t('missedCalls.filters.sections.type', 'Type'),
      rows: [
        {
          items: typeOptions,
        },
      ],
    },
    {
      title: t('missedCalls.filters.sections.status', 'Status'),
      rows: [
        {
          items: statusOptions,
        },
      ],
    },
    {
      title: t('missedCalls.filters.sections.user', 'User'),
      rows: [
        {
          items: userItems,
        },
      ],
    },
    {
      title: t('missedCalls.filters.sections.phoneNumber', 'Phone number'),
      rows: [
        {
          items: phoneNumberItems,
        },
      ],
    },
  ]
}

export const tryGetQueryFilters = (
  t: TFunction,
  selectedFilters: SelectedFilterData[] = [],
): MissedCallFilterParams => {
  const queryParams: MissedCallFilterParams = {}
  const typeTitle = t('missedCalls.filters.sections.type', 'Type')
  const statusTitle = t('missedCalls.filters.sections.status', 'Status')
  const userTitle = t('missedCalls.filters.sections.user', 'User')
  const phoneNumberTitle = t(
    'missedCalls.filters.sections.phoneNumber',
    'Phone number',
  )

  selectedFilters.forEach((filter) => {
    const { sectionTitle, item } = filter

    switch (sectionTitle) {
      case typeTitle:
        {
          const typeItem = item as MissedCallTypeItem
          queryParams.missed_type = queryParams.missed_type ?? []
          queryParams.missed_type.push(typeItem.data)
        }
        break
      case statusTitle:
        {
          const statusItem = item as MissedCallReturnStatusItem
          queryParams.missed_call_return_status =
            queryParams.missed_call_return_status ?? []
          queryParams.missed_call_return_status.push(statusItem.data)
        }
        break
      case userTitle:
        {
          const userItem = item as FollowUpUserItem
          queryParams.follow_up_users = queryParams.follow_up_users ?? []
          queryParams.follow_up_users.push(userItem.data)
        }
        break
      case phoneNumberTitle:
        {
          const phoneItem = item as PhoneNumberItem
          queryParams.to_formatted = queryParams.to_formatted ?? []
          queryParams.to_formatted.push(phoneItem.data)
        }
        break
      default:
        console.log(`Unknown Filter Section Title ${sectionTitle}`)
        break
    }
  })

  return queryParams
}

export const getActiveFilterDataFromSections = (
  filterSections: FilterSection[],
): SelectedFilterData[] => {
  return filterSections.flatMap((section) =>
    section.rows.flatMap((row) =>
      row.items
        .filter((item) => item.isSelected)
        .map((item) => ({
          sectionTitle: section.title || '',
          rowTitle: row.title || '',
          itemTitle: item.title || '',
          item,
        })),
    ),
  )
}

/**
 * Restores filter selections from SelectedFilterData array
 * Used to populate filter modal with previously selected filters
 */
export const getFilterSectionsFromSelectedFilters = (
  t: TFunction,
  selectedFilterData: SelectedFilterData[],
  pbxUsers: User[] = [],
  phoneNumbers: PhoneNumber[] = [],
): FilterSection[] => {
  const filterSections = getDefaultFilterData(t, pbxUsers, phoneNumbers)

  const selectedItemKeys = new Set(
    selectedFilterData.map((filter) =>
      getFilterItemKey(filter.sectionTitle, filter.rowTitle, filter.item),
    ),
  )

  return filterSections.map((section) => ({
    ...section,
    rows: section.rows.map((row) => ({
      ...row,
      items: row.items.map((item) => ({
        ...item,
        isSelected: selectedItemKeys.has(
          getFilterItemKey(section.title, row.title, item),
        ),
      })),
    })),
  }))
}
